import { useCallback, useMemo, useState } from 'react';
import type { IViewFilters, IWindowBounds } from '@d-orca/shared/client/activities';
import type { IListItem } from '../model/listItem';
import { readTrackerPrefs } from './trackerPrefs';
import { hasAreas, trackerActivities } from './trackerPopulation';

export interface ITrackerPopulation {
  /** The activities the tracker shows — what the page header counts. */
  activities: IListItem[];
  /** Whether the schedule has any activity with areas, filters aside — tells
   *  "nothing matches the filters" from "nothing to track". */
  hasLocated: boolean;
  /** The tracker's own "hide 100%" toggle (persisted with its prefs). */
  hideDone: boolean;
  setHideDone: (hideDone: boolean) => void;
  /** Clear every filter that narrows the tracker: the page's, and its own toggle. */
  clearFilters: () => void;
  /**
   * Hold the population while the user works the grid (QA F-61): from `hold`
   * until `release`, `activities` is the set that was shown at `hold` — live
   * values, held membership — so a tap that takes an activity to 100 % (Hide
   * 100 %) or out of a filter doesn't pull its column out from under the next
   * tap. `release` re-applies the filters. `hold` while held keeps the first set.
   */
  hold: () => void;
  release: () => void;
}

interface ITrackerPopulationInput {
  siteId: string;
  /** The page's prebuilt activities and their filter context (`useUnifiedList`). */
  activityItems: readonly IListItem[];
  today: string;
  restDays: Set<number>;
  filters: IViewFilters;
  window: IWindowBounds | null;
  /** The page's filter reset. */
  clear: () => void;
}

const activityId = (item: IListItem): string => item.raw.activity!.id;

/**
 * The Progress Tracker's population, held by the PAGE: the header counts it
 * and the tracker renders it, so the two can never disagree (F-48).
 */
export function useTrackerPopulation({
  siteId, activityItems, today, restDays, filters, window, clear,
}: ITrackerPopulationInput): ITrackerPopulation {
  const [hideDone, setHideDone] = useState(() => readTrackerPrefs(siteId).hideDone ?? false);
  const [heldIds, setHeldIds] = useState<ReadonlySet<string> | null>(null);
  const live = useMemo(
    () => trackerActivities(activityItems, filters, { today, restDays, window }, hideDone),
    [activityItems, filters, today, restDays, window, hideDone],
  );
  const located = useMemo(() => activityItems.filter((item) => hasAreas(item.raw.activity!)), [activityItems]);
  const activities = useMemo(
    () => (heldIds ? located.filter((item) => heldIds.has(activityId(item))) : live),
    [heldIds, located, live],
  );
  const hold = useCallback(() => setHeldIds((held) => held ?? new Set(live.map(activityId))), [live]);
  const release = useCallback(() => setHeldIds(null), []);
  const clearFilters = useCallback(() => {
    clear();
    setHideDone(false);
  }, [clear]);
  return { activities, hasLocated: located.length > 0, hideDone, setHideDone, clearFilters, hold, release };
}
